import { EmployeeCard } from '@/components/features/Employees/tables/EmployeeCard';
import { EmployeeTable } from '@/components/features/Employees/tables/EmployeeTable';
import { CreateButton } from '@/components/layout/CreateButton';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/shadcn/alert-dialog';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs';
import { useDebounce } from '@/hooks/use-debounce';
import { useIsMobile } from '@/hooks/use-mobile';
import { useServerFilters } from '@/hooks/use-server-filters';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { useCan } from '@/lib/utils';
import { type PaginationMeta, type User } from '@/types';
import type { PageProps as InertiaPageProps } from '@inertiajs/core';
import { Head, router, usePage } from '@inertiajs/react';
import { ShieldCheck, ShieldOff } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

type UsersPageProps = InertiaPageProps & {
    users: User[];
    pagination: PaginationMeta;
    filters?: {
        search?: string;
        role?: string;
        position?: string;
        company_id?: string;
        created_year?: number;
        status?: 'active' | 'suspended' | 'all' | null;
        per_page?: number;
        page?: number;
        sort_by?: string | null;
        sort_order?: 'asc' | 'desc' | null;
    };
    companies: Array<{ id: string; name: string }>;
    [key: string]: unknown;
};

// Maps a table column id to the `sort_by` token the backend understands.
const SORT_BY_MAP: Record<string, string> = {
    fullName: 'last_name',
    position: 'position',
    role: 'role',
    email: 'email',
    companies: 'company',
    status: 'status',
};

const columnIdForSortBy = (sortBy?: string | null): string | null => Object.entries(SORT_BY_MAP).find(([, value]) => value === sortBy)?.[0] ?? null;

export default function Users() {
    const { props } = usePage<UsersPageProps>();
    const { users, pagination, filters = {}, companies = [] } = props;
    const { t } = useTranslations();
    const { applyFilters, loading } = useServerFilters(filters, '/employees');
    const breadcrumbs = useBreadcrumbs('employees');
    const { can, isAdministrator } = useCan();
    const canSuspend = isAdministrator || can('employees.suspend');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentUserId = (props as any)?.auth?.user?.id as string | undefined;

    const isMobile = useIsMobile();
    const [viewMode, setViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [searchValue, setSearchValue] = useState(filters.search?.toString() ?? '');
    const debouncedSearchValue = useDebounce(searchValue, 800);
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [sortColumn, setSortColumn] = useState<string | null>(() => columnIdForSortBy(filters.sort_by));
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(() => filters.sort_order ?? null);
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(['fullName', 'position', 'role', 'email', 'companies', 'status']));
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmSuspend, setConfirmSuspend] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(() => ({
        role: filters.role ? [String(filters.role)] : [],
        position: filters.position ? [String(filters.position)] : [],
        company_id: filters.company_id ? [String(filters.company_id)] : [],
        status: filters.status ? [String(filters.status)] : [],
    }));
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [yearValues, setYearValues] = useState<string[]>(filters.created_year ? [String(filters.created_year)] : []);

    useEffect(() => {
        setViewMode(isMobile ? 'grid' : 'table');
    }, [isMobile]);

    useEffect(() => {
        const nextSearch = filters.search?.toString() ?? '';
        setSearchValue((previous) => (previous === nextSearch ? previous : nextSearch));
    }, [filters.search]);

    useEffect(() => {
        if ((filters.search?.toString() ?? '') === debouncedSearchValue) {
            return;
        }

        applyFilters({
            search: debouncedSearchValue || undefined,
            page: 1,
        });
    }, [applyFilters, debouncedSearchValue, filters.search]);

    useEffect(() => {
        setColumnFilters({
            role: filters.role ? [String(filters.role)] : [],
            position: filters.position ? [String(filters.position)] : [],
            company_id: filters.company_id ? [String(filters.company_id)] : [],
            status: filters.status ? [String(filters.status)] : [],
        });
        setYearValues(filters.created_year ? [String(filters.created_year)] : []);
    }, [filters.company_id, filters.created_year, filters.position, filters.role, filters.status]);

    const roleOptions = useMemo(() => {
        const uniqueRoles = Array.from(new Set(users.map((user) => user.role).filter((role): role is string => Boolean(role)))).sort();
        return uniqueRoles.map((role) => ({ value: role, label: role }));
    }, [users]);

    const positionOptions = useMemo(() => {
        const uniquePositions = Array.from(
            new Set(users.map((user) => user.position).filter((position): position is string => Boolean(position))),
        ).sort();
        return uniquePositions.map((position) => ({ value: position, label: position }));
    }, [users]);

    const companyOptions = useMemo(() => {
        return [...companies].sort((a, b) => a.name.localeCompare(b.name)).map((company) => ({ value: company.id, label: company.name }));
    }, [companies]);

    // Only administrators can see suspended employees, so the status filter is admin-only.
    // Suspended users are hidden by default ('active'); admins can opt into 'all' or 'suspended'.
    const statusOptions = useMemo(
        () =>
            isAdministrator
                ? [
                      { value: 'all', label: t('employees.tables.status_all', 'All') },
                      { value: 'active', label: t('employees.tables.status_active', 'Active') },
                      { value: 'suspended', label: t('employees.tables.status_suspended', 'Suspended') },
                  ]
                : [],
        [isAdministrator, t],
    );

    const columnDefinitions = [
        { id: 'fullName', label: t('employees.tables.colleague', 'Colleague') },
        { id: 'position', label: t('employees.tables.position', 'Position') },
        { id: 'role', label: t('employees.tables.role', 'Role') },
        { id: 'email', label: t('employees.tables.email', 'Email') },
        { id: 'companies', label: t('employees.tables.companies', 'Companies') },
        { id: 'status', label: t('employees.tables.status', 'Status') },
    ];

    const quickFilterDefinitions = [
        { id: 'role', label: t('employees.index.quick_filters.role', 'Role') },
        { id: 'position', label: t('employees.index.quick_filters.position', 'Position') },
        ...(isAdministrator ? [{ id: 'status', label: t('employees.index.quick_filters.status', 'Status') }] : []),
    ];

    const columnFilterOptions = {
        role: roleOptions,
        position: positionOptions,
        company_id: companyOptions,
        status: statusOptions,
    };

    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const years: string[] = [];
        for (let i = 0; i < 10; i += 1) {
            years.push(String(currentYear - i));
        }
        return years.map((year) => ({ value: year, label: year }));
    }, []);

    const filteredUsers = useMemo(() => users, [users]);

    const quickFilterNames = quickFilterDefinitions
        .filter((quickFilter) => (columnFilters[quickFilter.id] || []).length > 0)
        .map((quickFilter) => quickFilter.label);

    const quickFilterButtonLabel =
        quickFilterNames.length > 0
            ? `${t('employees.index.toolbar.quick_filters', 'Quick filters')}: ${quickFilterNames.join(', ')}`
            : t('employees.index.filters.all_employees', 'All Employees');

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setSortColumn(column);
        setSortDirection(direction);

        applyFilters({ sort_by: SORT_BY_MAP[column] || 'last_name', sort_order: direction, page: 1 });
    };

    const clearSort = () => {
        setSortColumn(null);
        setSortDirection(null);
        applyFilters({ sort_by: undefined, sort_order: undefined, page: 1 });
    };

    const handleFilterChange = (column: string, values: string[]) => {
        setColumnFilters((previous) => {
            const next = { ...previous };
            if (!values.length) {
                delete next[column];
            } else {
                next[column] = values;
            }
            return next;
        });

        const filterValue = values.length > 0 ? values[0] : undefined;
        if (column === 'role') {
            applyFilters({ role: filterValue, page: 1 });
        } else if (column === 'position') {
            applyFilters({ position: filterValue, page: 1 });
        } else if (column === 'company_id') {
            applyFilters({ company_id: filterValue, page: 1 });
        } else if (column === 'status') {
            applyFilters({ status: (filterValue as 'active' | 'suspended' | 'all' | undefined) ?? undefined, page: 1 });
        }
    };

    const toggleSingleFilter = (column: string, value: string) => {
        const current = columnFilters[column] || [];
        const exists = current.includes(value);

        // Status is logically single-select: picking an option replaces any prior choice.
        if (column === 'status') {
            handleFilterChange('status', exists ? [] : [value]);
            return;
        }

        const nextValues = exists ? current.filter((item) => item !== value) : [...current, value];
        handleFilterChange(column, nextValues);
    };

    const clearFilters = () => {
        setColumnFilters({});
        setSearchValue('');
        setYearValues([]);
        applyFilters({
            role: undefined,
            position: undefined,
            company_id: undefined,
            status: undefined,
            search: undefined,
            created_year: undefined,
            page: 1,
        });
    };

    const handlePageChange = (page: number) => {
        applyFilters({ page });
    };

    const handlePerPageChange = (perPage: number) => {
        applyFilters({ per_page: perPage, page: 1 });
    };

    const handleColumnHide = (column: string) => {
        setVisibleColumns((previous) => {
            const next = new Set(previous);
            next.delete(column);
            return next;
        });
    };

    const handleColumnShow = (column: string) => {
        setVisibleColumns((previous) => {
            const next = new Set(previous);
            next.add(column);
            return next;
        });
    };

    const handleYearChange = (values: string[]) => {
        setYearValues(values);
        const year = values[0];
        applyFilters({ created_year: year ? Number(year) : undefined, page: 1 });
    };

    const toggleSelect = (id: string) => {
        setSelectedIds((previous) => {
            const next = new Set(previous);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        const selectable = users.filter((u) => u.id !== currentUserId).map((u) => String(u.id));
        const allSelected = selectable.length > 0 && selectable.every((id) => selectedIds.has(id));
        setSelectedIds(allSelected ? new Set() : new Set(selectable));
    };

    const enterSelectionMode = () => {
        setSelectionMode(true);
        setSelectedIds(new Set());
    };

    const exitSelectionMode = () => {
        setSelectionMode(false);
        setSelectedIds(new Set());
    };

    const selectedUsers = useMemo(() => users.filter((user) => selectedIds.has(String(user.id))), [users, selectedIds]);
    const allSelectedSuspended = selectedUsers.length > 0 && selectedUsers.every((user) => Boolean(user.suspended_at));
    const dialogSuspendMode = !allSelectedSuspended;

    const openConfirm = () => {
        if (selectedIds.size === 0) {
            toast.error(t('employees.index.suspend_dialog.no_selection', 'Select at least one employee.'));
            return;
        }
        setConfirmSuspend(dialogSuspendMode);
        setConfirmOpen(true);
    };

    const handleConfirm = () => {
        if (selectedIds.size === 0) return;
        setSubmitting(true);
        router.post(
            route('employees.bulk-suspend'),
            { ids: Array.from(selectedIds), suspend: confirmSuspend },
            {
                preserveScroll: true,
                onSuccess: () => {
                    toast.success(t('employees.index.suspend_dialog.success', 'Employees updated.'));
                    setConfirmOpen(false);
                    exitSelectionMode();
                },
                onError: () => {
                    toast.error(t('employees.index.suspend_dialog.error', 'Failed to update employees.'));
                },
                onFinish: () => setSubmitting(false),
            },
        );
    };

    const sortableColumns = [
        { id: 'fullName', label: t('employees.tables.colleague', 'Colleague') },
        { id: 'position', label: t('employees.tables.position', 'Position') },
        { id: 'role', label: t('employees.tables.role', 'Role') },
        { id: 'email', label: t('employees.tables.email', 'Email') },
        { id: 'status', label: t('employees.tables.status', 'Status') },
    ];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('employees.index.title', 'Employees')} />
            <PageHeader
                title={t('employees.index.heading', 'Employees')}
                subtitle={t('employees.index.subheading', 'Manage your team members')}
                actions={
                    <div className="flex flex-wrap items-center gap-2">
                        {canSuspend && !selectionMode && (
                            <Button variant="outline" onClick={enterSelectionMode}>
                                <ShieldOff className="size-4" />
                                {t('employees.index.actions.suspend_employees', 'Suspend Employees')}
                            </Button>
                        )}
                        {canSuspend && selectionMode && (
                            <>
                                <Button variant="ghost" onClick={exitSelectionMode}>
                                    {t('employees.index.actions.cancel_selection', 'Cancel')}
                                </Button>
                                <Button
                                    variant={dialogSuspendMode ? 'destructive' : 'default'}
                                    onClick={openConfirm}
                                    disabled={selectedIds.size === 0}
                                >
                                    {dialogSuspendMode ? <ShieldOff className="size-4" /> : <ShieldCheck className="size-4" />}
                                    {dialogSuspendMode
                                        ? t('employees.index.actions.suspend_selected', 'Suspend selected (:count)').replace(
                                              ':count',
                                              String(selectedIds.size),
                                          )
                                        : t('employees.index.actions.unsuspend_selected', 'Unsuspend selected (:count)').replace(
                                              ':count',
                                              String(selectedIds.size),
                                          )}
                                </Button>
                            </>
                        )}
                        <CreateButton
                            href="/employees/create"
                            permission="employees.create"
                            label={t('employees.index.actions.create', 'Create Employee')}
                        />
                    </div>
                }
            />

            <PageContent>
                <GenericToolbar
                    quickFilterButtonLabel={quickFilterButtonLabel}
                    quickFilterDefinitions={quickFilterDefinitions}
                    columnDefinitions={columnDefinitions}
                    columnFilterOptions={columnFilterOptions}
                    columnFilters={columnFilters}
                    columnFilterSearch={columnFilterSearch}
                    setColumnFilterSearch={setColumnFilterSearch}
                    toggleSingleFilter={toggleSingleFilter}
                    handleFilterChange={handleFilterChange}
                    clearFilters={clearFilters}
                    isFilterOpen={isFilterOpen}
                    setIsFilterOpen={setIsFilterOpen}
                    isSortOpen={isSortOpen}
                    setIsSortOpen={setIsSortOpen}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    handleSortChange={handleSortChange}
                    clearSort={clearSort}
                    search={searchValue}
                    setSearch={setSearchValue}
                    perPage={pagination.per_page}
                    onPerPageChange={handlePerPageChange}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    visibleColumns={visibleColumns}
                    handleColumnShow={handleColumnShow}
                    handleColumnHide={handleColumnHide}
                    filteredCount={pagination.total}
                    resourceName="employee"
                    resourceNamePlural="employees"
                    sortableColumns={sortableColumns}
                    excludeFromMoreFilters={['role', 'position', 'status']}
                    customFilters={
                        <div className="space-y-2">
                            <label className="text-sm font-medium">{t('employees.index.filters.year_placeholder', 'Filter by created year')}</label>
                            <select
                                className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
                                value={yearValues[0] || ''}
                                onChange={(event) => handleYearChange(event.target.value ? [event.target.value] : [])}
                            >
                                <option value="">{t('common.all', 'All')}</option>
                                {yearOptions.map((year) => (
                                    <option key={year.value} value={year.value}>
                                        {year.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    }
                />

                {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {filteredUsers.map((user) => (
                            <EmployeeCard key={user.id} employee={user} />
                        ))}
                    </div>
                ) : (
                    <EmployeeTable
                        users={filteredUsers}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        onColumnHide={handleColumnHide}
                        onColumnShow={handleColumnShow}
                        visibleColumns={visibleColumns}
                        columnFilters={columnFilters}
                        columnFilterOptions={columnFilterOptions}
                        onFilterChange={handleFilterChange}
                        selectionMode={selectionMode}
                        selectedIds={selectedIds}
                        onToggleSelect={toggleSelect}
                        onToggleSelectAll={toggleSelectAll}
                        currentUserId={currentUserId}
                    />
                )}

                <PaginationControls
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onPerPageChange={handlePerPageChange}
                    loading={loading}
                    className="mt-6"
                />

                <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                {confirmSuspend
                                    ? t('employees.index.suspend_dialog.title', 'Suspend employees')
                                    : t('employees.index.suspend_dialog.unsuspend_title', 'Unsuspend employees')}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                {(confirmSuspend
                                    ? t(
                                          'employees.index.suspend_dialog.description',
                                          'You are about to suspend :count employee(s). Suspended users cannot log in and will be signed out of all sessions.',
                                      )
                                    : t(
                                          'employees.index.suspend_dialog.unsuspend_description',
                                          'You are about to restore access for :count employee(s).',
                                      )
                                ).replace(':count', String(selectedIds.size))}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel disabled={submitting}>{t('employees.index.suspend_dialog.cancel', 'Cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleConfirm();
                                }}
                                disabled={submitting}
                            >
                                {t('employees.index.suspend_dialog.confirm', 'Confirm')}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {viewMode === 'grid' && filteredUsers.length === 0 && (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <p className="text-muted-foreground">
                                {t('employees.index.empty.no_employees_found', 'No employees found matching your filters.')}
                            </p>
                        </CardContent>
                    </Card>
                )}
            </PageContent>
        </AppLayout>
    );
}
